import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaCalendar,
  FaClock,
  FaEye,
  FaUser,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaImage,
  FaVideo,
  FaTag,
  FaGlobe,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaUsers,
  FaChartBar,
  FaTrophy,
  FaStar,
  FaTimes,
  FaInfoCircle
} from "react-icons/fa";

const FreeCompletedAds = () => {
  const [ads, setAds] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredAds, setFilteredAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMedia, setPreviewMedia] = useState({ url: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedProofDetails, setExpandedProofDetails] = useState({});
  const rowsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/completed/free/ad`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched completed Ads Data:", response.data);
        setAds(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  useEffect(() => {
    // Sort ads by updatedAt descending (newest first)
    const sortedAds = [...ads].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    if (!search) {
      setFilteredAds(sortedAds);
      setCurrentPage(1);
      return;
    }
    const lowerSearch = search.toLowerCase();
    const filtered = sortedAds.filter(ad => {
      const adid = ad._id?.toLowerCase() || '';
      const adtype = ad.adType?.toLowerCase() || '';
      const dateStr = ad.updatedAt ? new Date(ad.updatedAt).toLocaleDateString("en-GB") : '';
      return (
        adid.includes(lowerSearch) ||
        adtype.includes(lowerSearch) ||
        dateStr.includes(lowerSearch)
      );
    });
    setFilteredAds(filtered);
    setCurrentPage(1);
  }, [ads, search]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-500 animate-spin"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-teal-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-cyan-500 animate-spin" style={{ animationDuration: '2s' }}></div>
        </div>
        <p className="text-lg font-semibold text-gray-700">Loading Free Completed Ads...</p>
        <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center border border-red-200">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaTimesCircle className="text-red-500 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedAds = filteredAds.slice(startIdx, endIdx);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Modern Header */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 rounded-3xl shadow-xl p-4 sm:p-8 text-white relative overflow-hidden">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="p-3 sm:p-4 bg-white/20 rounded-2xl backdrop-blur-md shrink-0">
                <FaTrophy className="text-xl sm:text-3xl text-yellow-300" />
              </div>
              <div>
                <h1 className="text-xl sm:text-4xl font-black tracking-tight leading-none uppercase">Completed Ads</h1>
                <p className="text-emerald-100 mt-2 text-[10px] sm:text-sm font-bold uppercase tracking-widest opacity-80">Past Campaign Archive</p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-3">
              <button
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm border border-white/10"
                onClick={() => navigate("/admin/freependingads")}
              >
                <FaClock className="text-amber-300" /> PENDING
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm border border-white/10"
                onClick={() => navigate("/admin/freerunningads")}
              >
                <FaPlay className="text-blue-300" /> RUNNING
              </button>
              <button
                className="col-span-2 lg:col-span-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm border border-white/10"
                onClick={() => navigate("/admin/freeapprovedads")}
              >
                <FaCheckCircle className="text-emerald-300" /> ACCEPTED
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Stats Section */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 mb-6 sm:mb-10">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-4 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative group">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3 sm:py-4 bg-slate-50 border-0 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium text-slate-700 placeholder:text-slate-400"
                placeholder="Search Ad ID, Ad Type, or Date..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3 px-1">
              <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-tighter">
                {filteredAds.length} ADS ARCHIVED
              </div>
              <button
                className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 sm:p-3 rounded-xl transition-colors"
                onClick={() => setSearch('')}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 pb-20">
        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden">
          <div className="hidden lg:block">
            {/* Desktop Table */}
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Media</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Ad Identifier</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Target Info</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Location</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Statistics</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Timeline</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedAds.map((ad) => (
                  <tr key={ad._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="relative w-14 h-14 rounded-2xl overflow-hidden shadow-sm group-hover:scale-110 transition-transform">
                        {ad.mediaType?.toLowerCase() === "video" ? (
                          <video src={ad.videoUrl} className="w-full h-full object-cover" />
                        ) : (
                          <img src={ad.imageUrl} className="w-full h-full object-cover" alt="Ad" />
                        )}
                        <div
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                          onClick={() => {
                            setPreviewMedia({ url: ad.mediaType?.toLowerCase() === "video" ? ad.videoUrl : ad.imageUrl, type: ad.mediaType?.toLowerCase() });
                            setShowPreviewModal(true);
                          }}
                        >
                          <FaEye className="text-white text-lg" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-black text-slate-800 text-sm tracking-tight">{ad._id}</p>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{ad.adType}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter ${ad.userType === 'influencer' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {ad.userType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs font-bold text-slate-800">{ad.state}</p>
                      <p className="text-[10px] text-slate-400 font-medium truncate max-w-[150px]">
                        {Array.isArray(ad.city) ? ad.city.filter(Boolean).join(", ") : ad.city || "N/A"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${ad.isFullyCompleted ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${ad.completionRate || 0}%` }}
                          />
                        </div>
                        <p className="text-[10px] font-black text-slate-600 uppercase">
                          {ad.totalCompleted || 0}/{ad.requiredReportersCount || 0} Submissions
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs font-bold text-slate-800">{new Date(ad.updatedAt).toLocaleDateString()}</p>
                      <p className="text-[10px] text-slate-400 font-medium uppercase">{ad.status}</p>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        className="bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2"
                        onClick={() => { setSelectedAd(ad); setShowModal(true); }}
                      >
                        <FaEye /> DETAILS
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden divide-y divide-slate-50">
            {/* Mobile Cards */}
            {paginatedAds.length === 0 ? (
              <div className="p-12 text-center">
                <FaTrophy className="text-4xl text-slate-200 mx-auto mb-4" />
                <p className="font-bold text-slate-400">NO COMPLETED ADS FOUND</p>
              </div>
            ) : (
              paginatedAds.map((ad) => (
                <div key={ad._id} className="p-4 flex flex-col gap-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative w-16 h-16 rounded-2xl overflow-hidden shadow-sm shrink-0 border border-slate-100">
                        {ad.mediaType?.toLowerCase() === "video" ? (
                          <video src={ad.videoUrl} className="w-full h-full object-cover" />
                        ) : (
                          <img src={ad.imageUrl} className="w-full h-full object-cover" alt="Ad" />
                        )}
                        <div
                          className="absolute inset-0 bg-black/40 flex items-center justify-center"
                          onClick={() => {
                            setPreviewMedia({ url: ad.mediaType?.toLowerCase() === "video" ? ad.videoUrl : ad.imageUrl, type: ad.mediaType?.toLowerCase() });
                            setShowPreviewModal(true);
                          }}
                        >
                          <FaEye className="text-white text-sm" />
                        </div>
                      </div>
                      <div>
                        <p className="font-black text-slate-900 text-sm">{ad._id}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter ${ad.userType === 'influencer' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {ad.userType}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400 uppercase">{ad.adType}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
                      onClick={() => { setSelectedAd(ad); setShowModal(true); }}
                    >
                      <FaEye />
                    </button>
                  </div>

                  <div className="bg-slate-50 rounded-2xl p-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</p>
                      <p className="text-[10px] font-black text-emerald-600 uppercase">{ad.status}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Submissions</p>
                      <p className="text-[10px] font-black text-slate-800">{ad.totalCompleted || 0}/{ad.requiredReportersCount || 0}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Target Area</p>
                      <p className="text-[10px] font-bold text-slate-800 truncate">{ad.state} • {Array.isArray(ad.city) ? ad.city.filter(Boolean).slice(0, 2).join(", ") : ad.city || "N/A"}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Responsive Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-10">
            <nav className="inline-flex items-center space-x-2 bg-white rounded-3xl shadow-xl p-3 border border-slate-100">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              >
                <FaArrowLeft className="text-sm" />
              </button>
              <div className="flex items-center gap-1.5 px-2">
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl font-black text-xs sm:text-sm transition-all ${currentPage === pageNum ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-100' : 'text-slate-400 hover:bg-slate-50'}`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              >
                <FaArrowRight className="text-sm" />
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Media Preview Modal */}
      {showPreviewModal && previewMedia.url && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => {
          setShowPreviewModal(false);
          setPreviewMedia({ url: '', type: '' });
        }}>
          <div className="max-w-4xl max-h-[90vh] relative bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {previewMedia.type === 'video' ? <FaVideo /> : <FaImage />}
                Media Preview
              </h3>
              <button
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewMedia({ url: '', type: '' });
                }}
              >
                <FaTimesCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  className="max-h-[70vh] max-w-full rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={previewMedia.url}
                  alt="Preview"
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedAd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FaInfoCircle />
                Advertisement Details
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Ad ID</label>
                    <p className="text-gray-800">{selectedAd._id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Ad Type</label>
                    <p className="text-gray-800">{selectedAd.adType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Status</label>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      {selectedAd.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Media Type</label>
                    <p className="text-gray-800">{selectedAd.mediaType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Required Reporters</label>
                    <p className="text-gray-800">{selectedAd.requiredReportersCount}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">All State</label>
                    <p className="text-gray-800">{selectedAd.allState ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">State</label>
                    <p className="text-gray-800">
                      {Array.isArray(selectedAd.state) ? selectedAd.state.filter(Boolean).join(', ') : selectedAd.state}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">City</label>
                    <p className="text-gray-800">
                      {Array.isArray(selectedAd.city) ? selectedAd.city.filter(Boolean).join(', ') : selectedAd.city}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Description</h3>
                <p className="text-gray-800">{selectedAd.description}</p>
              </div>

              {/* Media Section */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Media</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedAd.imageUrl && selectedAd.imageUrl.trim() !== '' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Image</label>
                      <img
                        src={selectedAd.imageUrl}
                        alt="Ad Media"
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    </div>
                  )}
                  {selectedAd.videoUrl && selectedAd.videoUrl.trim() !== '' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Video</label>
                      <video
                        src={selectedAd.videoUrl}
                        controls
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Completed Users Section */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaUsers />
                    Completed Users ({selectedAd.completedReporters?.length || 0})
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Total Accepted:</span>
                      <span className="font-semibold text-blue-600">{selectedAd.totalAccepted || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completed:</span>
                      <span className="font-semibold text-green-600">{selectedAd.totalCompleted || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Required:</span>
                      <span className="font-semibold text-gray-600">{selectedAd.totalRequired || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completion Rate:</span>
                      <span className={`font-semibold ${selectedAd.isFullyCompleted
                        ? 'text-emerald-600'
                        : 'text-yellow-600'
                        }`}>
                        {selectedAd.completionRate || 0}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedAd.isFullyCompleted
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {selectedAd.isFullyCompleted ? '✅ Fully Complete' : '⏳ In Progress'}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedAd.completedReporters && selectedAd.completedReporters.length > 0 ? (
                  <div className="overflow-y-auto max-h-80 space-y-3 py-2">
                    {selectedAd.completedReporters.map((user, index) => (
                      <div key={user.reporterId || index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">iinsaf ID</label>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">
                                  {user.iinsafId?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-800">{user.iinsafId}</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">User Role</label>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.userRole === 'Influencer'
                              ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border border-purple-200'
                              : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200'
                              }`}>
                              <FaUser className="w-3 h-3 mr-1" />
                              {user.userRole || 'Reporter'}
                            </span>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                              <FaCheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </span>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Completed At</label>
                            <div className="flex flex-col">
                              <p className="text-sm font-medium text-gray-800">
                                {user.completedAt ? new Date(user.completedAt).toLocaleDateString() : '-'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {user.completedAt ? new Date(user.completedAt).toLocaleTimeString() : '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {user.acceptedAt && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Accepted: {new Date(user.acceptedAt).toLocaleString()}</span>
                              <span>Reporter ID: {user.reporterId}</span>
                            </div>
                          </div>
                        )}

                        {/* ✅ Complete Work Submitted Details */}
                        {user.proofDetails && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <FaCheckCircle className="w-4 h-4 text-green-600" />
                                Submitted Work Details
                              </h4>
                              <button
                                onClick={() => {
                                  const key = `${user.reporterId}-${user.iinsafId}`;
                                  setExpandedProofDetails(prev => ({
                                    ...prev,
                                    [key]: !prev[key]
                                  }));
                                }}
                                className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <FaEye className="w-3 h-3" />
                                <span>
                                  {expandedProofDetails[`${user.reporterId}-${user.iinsafId}`] ? 'Hide Details' : 'View Details'}
                                </span>
                              </button>
                            </div>

                            {/* ✅ Collapsible Proof Details */}
                            {expandedProofDetails[`${user.reporterId}-${user.iinsafId}`] && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Screenshot */}
                                  {user.proofDetails.screenshot && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Screenshot</label>
                                      <div className="relative group">
                                        <img
                                          src={user.proofDetails.screenshot}
                                          alt="Proof Screenshot"
                                          className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:shadow-md transition-all duration-200"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                          onClick={() => {
                                            // ✅ Handle both Cloudinary URLs and local paths
                                            const imageUrl = user.proofDetails.screenshot.startsWith('http')
                                              ? user.proofDetails.screenshot
                                              : `${process.env.REACT_APP_API_BASE_URL}/${user.proofDetails.screenshot}`;
                                            setPreviewMedia({ url: imageUrl, type: 'image' });
                                            setShowPreviewModal(true);
                                          }}
                                        />
                                        <div className="absolute inset-0 bg-gray-100 rounded-lg border flex items-center justify-center" style={{ display: 'none' }}>
                                          <div className="text-center">
                                            <FaImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-xs text-gray-500">Image not available</p>
                                          </div>
                                        </div>
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                                          <FaEye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Video Link Button */}
                                  {user.proofDetails.videoLink && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Video Link</label>
                                      <div className="w-full h-32 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center">
                                        <button
                                          onClick={() => window.open(user.proofDetails.videoLink, '_blank')}
                                          className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                                        >
                                          <FaPlay className="w-4 h-4" />
                                          <span>Watch Video</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Channel and Platform Details */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Channel Name</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {user.proofDetails.channelName || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {user.proofDetails.platform || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {user.proofDetails.duration || 'N/A'}
                                    </p>
                                  </div>
                                </div>

                                {/* Submission Details */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Submitted At</label>
                                      <p className="text-sm text-gray-800">
                                        {user.proofDetails.submittedAt ? new Date(user.proofDetails.submittedAt).toLocaleString() : 'N/A'}
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Proof Status</label>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${user.proofDetails.status === 'completed'
                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        }`}>
                                        <FaCheckCircle className="w-3 h-3 mr-1" />
                                        {user.proofDetails.status || 'Unknown'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Admin Reject Note (if any) */}
                                {user.proofDetails.adminRejectNote && (
                                  <div className="mt-4 pt-4 border-t border-red-100">
                                    <label className="block text-xs font-medium text-red-600 mb-1">Admin Reject Note</label>
                                    <p className="text-sm text-red-800 bg-red-50 px-3 py-2 rounded-lg">
                                      {user.proofDetails.adminRejectNote}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaUsers className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">No completed users found</p>
                    <p className="text-sm text-gray-400 mt-1">Users who have completed their proofs will appear here</p>
                  </div>
                )}
              </div>



              {/* Timestamps Section */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Timestamps</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created At</label>
                    <p className="text-gray-800">{new Date(selectedAd.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Updated At</label>
                    <p className="text-gray-800">{new Date(selectedAd.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowModal(false)}
                className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaTimes />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeCompletedAds;
